// ── Polymarket order queue metrics from a CLOB book ─────────────
//
// Polymarket has no per-order FIFO slot endpoint (Kalshi does), so the
// only queue facts we can state EXACTLY are the ones derivable from the
// public book depth: how many contracts rest at strictly-better prices
// (queueAbove) and the spread to the best price (bookDiff). The absolute
// FIFO position can't be known exactly — we report a PESSIMISTIC upper
// bound (assume our order sits at the back of its own price level) so the
// number never tells a trader they're closer to filling than they are.
//
// BUY-oriented: pass the bid ladder for the order's own token (best-bid
// first). Sells are not computed here (the ask side needs the opposite
// token's book; resting sells are close-path only) — callers leave the
// queue fields null for sells rather than risk a wrong number.

import type { BookLevel } from '../../types';

export interface PolyOrderQueue {
  /** Cents behind the best bid on our ladder. 0 = at the front (our price
   *  IS the best bid). null when the book is empty/unknown. */
  bookDiff: number | null;
  /** EXACT: contracts resting at strictly-better prices than ours — they
   *  all fill before our level is touched. 0 = top of book. */
  queueAbove: number | null;
  /** Contracts resting at our exact price level (includes our own
   *  remaining). Exact from the book. */
  atLevelDepth: number | null;
  /** Pessimistic upper-bound absolute FIFO position = queueAbove +
   *  atLevelDepth (assumes everyone at our level is ahead of us). Real
   *  position is in [queueAbove, this]. Safe/conservative for the "am I
   *  about to fill" read. */
  queue: number | null;
  /** Total contracts resting on our ladder (both above and at/below). */
  queueTotal: number | null;
}

const EMPTY: PolyOrderQueue = {
  bookDiff: null,
  queueAbove: null,
  atLevelDepth: null,
  queue: null,
  queueTotal: null,
};

/**
 * @param bids   the order token's own bid ladder (best-bid first), cents.
 * @param ownPriceCents  the resting order's price in cents.
 */
export function polyOrderQueueFromBook(
  bids: BookLevel[] | null | undefined,
  ownPriceCents: number | null,
): PolyOrderQueue {
  if (ownPriceCents == null || !Number.isFinite(ownPriceCents)) return EMPTY;
  if (!bids || bids.length === 0) {
    // No book: we can still say we're at front by definition of "no one
    // ahead", but an absent feed is not the same as an empty book — report
    // unknown so a dead subscription can't render a false "front of book".
    return EMPTY;
  }
  let queueAbove = 0;
  let atLevelDepth = 0;
  let queueTotal = 0;
  let bestBid: number | null = null;
  for (const lvl of bids) {
    const price = lvl.priceCents;
    const size = lvl.size;
    if (!Number.isFinite(price) || !Number.isFinite(size) || size <= 0) continue;
    if (bestBid == null || price > bestBid) bestBid = price;
    queueTotal += size;
    if (price > ownPriceCents) queueAbove += size;
    else if (price === ownPriceCents) atLevelDepth += size;
  }
  if (bestBid == null) return EMPTY;
  const bookDiff = Math.max(0, bestBid - ownPriceCents);
  return {
    bookDiff,
    queueAbove,
    atLevelDepth,
    queue: queueAbove + atLevelDepth,
    queueTotal,
  };
}
